import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useHackers } from "@/hooks/useHackers";

const HACKERS_PER_PAGE = 6;

function StarRating({ rating }: { rating: number | null }) {
  const r = rating ?? 0;
  const fullStars = Math.floor(r);
  const hasHalf = r % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: fullStars }).map((_, i) => (
        <i key={`full-${i}`} className="fas fa-star text-xs text-yellow-400" />
      ))}
      {hasHalf && (
        <i className="fas fa-star-half-alt text-xs text-yellow-400" />
      )}
      {Array.from({ length: emptyStars }).map((_, i) => (
        <i key={`empty-${i}`} className="far fa-star text-xs text-gray-300" />
      ))}
    </div>
  );
}

function HackerSkeleton() {
  return (
    <div className="bg-[#0d1117] border border-gray-700 rounded p-4 md:p-5 animate-pulse">
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-gray-200 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/5" />
          <div className="h-3 bg-gray-200 rounded w-1/3" />
          <div className="h-6 bg-gray-200 rounded w-1/4" />
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-gray-800 flex gap-3">
        <div className="h-3 bg-gray-200 rounded w-16" />
        <div className="h-3 bg-gray-200 rounded w-14" />
        <div className="h-3 bg-gray-200 rounded w-20" />
      </div>
    </div>
  );
}

export default function HackersSection() {
  const { hackers, loading } = useHackers();
  const [activePage, setActivePage] = useState(1);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(hackers.length / HACKERS_PER_PAGE)), [hackers.length]);
  const startIndex = (activePage - 1) * HACKERS_PER_PAGE;
  const currentHackers = useMemo(() => hackers.slice(startIndex, startIndex + HACKERS_PER_PAGE), [hackers, activePage]);

  const handlePageClick = (page: number) => {
    setActivePage(page);
    document.getElementById("hackers-section")?.scrollIntoView({ behavior: "smooth" });
  };

  const getPageNumbers = (): (number | "...")[] => {
    const pages: (number | "...")[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (activePage > 3) pages.push("...");
      const start = Math.max(2, activePage - 1);
      const end = Math.min(totalPages - 1, activePage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (activePage < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <section id="hackers-section" className="py-12 md:py-16 bg-[#0d1117]">
      <div className="w-full px-4 md:px-8 max-w-6xl mx-auto">
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-light text-gray-200">
            Find and Hire a Professional Hacker
          </h2>
          <div className="mt-3 inline-block border-b-2 border-[#5bc0de] pb-1">
            <span className="text-sm font-semibold text-gray-300">Top Hackers</span>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: HACKERS_PER_PAGE }).map((_, i) => (
              <HackerSkeleton key={i} />
            ))}
          </div>
        ) : hackers.length === 0 ? (
          <div className="text-center py-12">
            <i className="fas fa-user-secret text-4xl text-gray-300 mb-3 block" />
            <p className="text-gray-500 text-sm">No hackers available right now. Check back soon!</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {currentHackers.map((hacker) => (
                <div
                  key={hacker.id}
                  className="bg-[#0d1117] border border-gray-700 rounded p-4 md:p-5"
                >
                  <div className="flex items-start gap-4">
                    <img
                      src={hacker.avatar}
                      alt={hacker.name}
                      className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <Link
                            to={`/hacker/${hacker.id}`}
                            className="text-base font-semibold text-gray-200 hover:text-[#5bc0de] block truncate"
                          >
                            {hacker.verified && (
                              <i className="fas fa-check-circle text-[#5bc0de] text-xs mr-1" />
                            )}
                            {hacker.name}
                            {hacker.featured && (
                              <span className="ml-2 text-[10px] bg-[#f0ad4e]/20 text-[#f0ad4e] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wide">Featured</span>
                            )}
                          </Link>
                          <span className="text-xs text-[#5bc0de]">{hacker.specialty}</span>
                          <span className="text-xs text-gray-400 ml-2"><i className="fas fa-map-marker-alt mr-1" />{hacker.country}</span>
                          <span className="text-xs text-gray-400 ml-2">{hacker.age != null ? `${hacker.age} yrs` : "—"}</span>
                        </div>
                        <Link
                          to={`/hacker/${hacker.id}`}
                          className="flex-shrink-0 inline-block bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-xs font-semibold px-4 py-1.5 rounded transition-all whitespace-nowrap"
                        >
                          View Profile
                        </Link>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-800 flex flex-wrap items-center gap-4">
                    <StarRating rating={hacker.rating} />
                    <span className="text-sm text-gray-400">{hacker.rate}</span>
                    <span className="text-sm text-gray-500">{hacker.experience}</span>
                    {hacker.certifications && hacker.certifications.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 w-full">
                        {hacker.certifications.slice(0, 3).map((cert) => (
                          <span
                            key={cert}
                            className="text-[10px] bg-[#5bc0de]/10 text-[#5bc0de] px-2 py-0.5 rounded font-semibold uppercase tracking-wide"
                          >
                            <i className="fas fa-certificate mr-1" />{cert}
                          </span>
                        ))}
                        {hacker.certifications.length > 3 && (
                          <span className="text-[10px] text-gray-400 px-1 py-0.5 font-medium">
                            +{hacker.certifications.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                    {hacker.skills && hacker.skills.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {hacker.skills.map((skill) => (
                          <span
                            key={skill}
                            className="text-xs bg-[#1c2128] text-gray-400 px-2 py-1 rounded"
                          >
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                onClick={() => handlePageClick(Math.max(1, activePage - 1))}
                disabled={activePage === 1}
                className="w-8 h-8 flex items-center justify-center rounded text-sm text-gray-400 hover:text-gray-400 disabled:opacity-30 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="fas fa-angle-left" />
              </button>
              {getPageNumbers().map((p, i) =>
                p === "..." ? (
                  <span key={`dots-${i}`} className="text-sm text-gray-400 px-1">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => handlePageClick(p)}
                    className={`w-8 h-8 flex items-center justify-center rounded text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                      activePage === p ? "bg-[#5bc0de] text-white" : "text-gray-400 hover:bg-[#1c2128]"
                    }`}
                  >
                    {p}
                  </button>
                )
              )}
              <button
                onClick={() => handlePageClick(Math.min(totalPages, activePage + 1))}
                disabled={activePage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded text-sm text-gray-400 hover:text-gray-400 disabled:opacity-30 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="fas fa-angle-right" />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}